/**
 * CMS 訂單詳情（GET /admin/orders/:id）。
 *
 * 顯示訂單 header（order_number / channel / state / total / 時間）+ items 表格
 * （每 item 含 unit_price / line_total / 套用 pricing rule 資訊）。
 *
 * 對應 Bug 2 修復後之逐筆 drill-down：可看到 server-resolved unit_price 與
 * pricing_id FK 對到的折扣規則，讓 CMS 操作者驗證定價推廣實際套用情形。
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill, type PillTone } from '../components/Pill';
import { getAdminOrder, type AdminOrderItem, type OrderState } from '../lib/api-client';
import { ticketTypeLabel } from '../lib/ticket-type-label';

const STATE_TONE: Record<OrderState, PillTone> = {
  HOLD: 'warning',
  PAYMENT_PENDING: 'warning',
  PAID: 'success',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
  PAYMENT_FAILED: 'danger',
  OVERSOLD: 'danger',
};

const STATE_LABEL: Record<OrderState, string> = {
  HOLD: '保留中',
  PAYMENT_PENDING: '付款進行中',
  PAID: '已付款',
  EXPIRED: '已過期',
  CANCELLED: '已取消',
  PAYMENT_FAILED: '付款失敗',
  OVERSOLD: '超賣',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function formatTwd(amount: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `NT$ ${Math.round(n).toLocaleString('en-US')}`;
}

/**
 * 明細金額——保留小數，不四捨五入到元。
 *
 * 套票的各段金額是打包價分攤出來的（1300 拆成 1039.91 / 260.09），四捨五入後各列
 * 相加可能對不上總額，看起來像系統算錯。訂單頁是對帳用的，這裡要顯示實際落庫的值。
 */
function formatTwdExact(amount: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return `NT$ ${n.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function pickI18n(o: Record<string, string> | null, fallback: string): string {
  if (!o) return fallback;
  return o['zh-TW'] ?? o['en'] ?? Object.values(o)[0] ?? fallback;
}

function ruleSummary(it: AdminOrderItem): string {
  if (!it.pricing_rule) return '—';
  const r = it.pricing_rule;
  if (r.promotion_type === 'discount_pct') {
    return `折扣 ${Number(r.discount_pct).toFixed(0)}%（原價 ${formatTwd(r.list_price)}）`;
  }
  if (r.promotion_type === 'fixed_amount_off') {
    return `折抵 ${formatTwd(r.fixed_amount_off ?? '0')}（原價 ${formatTwd(r.list_price)}）`;
  }
  return r.promotion_type;
}

export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const orderQuery = useQuery({
    queryKey: ['admin.orders.detail', id],
    queryFn: () => getAdminOrder(id ?? ''),
    enabled: id !== undefined,
  });

  const order = orderQuery.data;

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">訂單詳情</h1>
        <div className="page-head__actions">
          <Button variant="secondary" onClick={() => navigate('/admin/orders')}>
            ← 返回列表
          </Button>
        </div>
      </div>

      {orderQuery.isLoading && <div>載入中…</div>}
      {orderQuery.error && (
        <div className="error-banner">載入失敗：{(orderQuery.error as Error).message}</div>
      )}

      {order && (
        <>
          <section className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
            <table style={{ width: '100%', maxWidth: 720 }}>
              <tbody>
                <tr>
                  <th style={{ width: 140, textAlign: 'left' }}>訂單編號</th>
                  <td
                    data-testid="order-number"
                    style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700 }}
                  >
                    {order.order_number}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>通路</th>
                  <td>
                    <Pill dot={false}>{order.channel}</Pill>
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>狀態</th>
                  <td>
                    <Pill tone={STATE_TONE[order.state] ?? 'neutral'} dot={false}>
                      {STATE_LABEL[order.state] ?? order.state}
                    </Pill>
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>小計</th>
                  <td>{formatTwd(order.subtotal)}</td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>總額</th>
                  <td
                    data-testid="order-total"
                    style={{ fontSize: 18, fontWeight: 700, color: 'var(--c-accent)' }}
                  >
                    {formatTwd(order.total)}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>建立時間</th>
                  <td style={{ fontSize: 13 }}>{formatDateTime(order.created_at)}</td>
                </tr>
                <tr>
                  <th style={{ textAlign: 'left' }}>最後更新</th>
                  <td style={{ fontSize: 13 }}>{formatDateTime(order.updated_at)}</td>
                </tr>
                {order.state === 'HOLD' && (
                  <tr>
                    <th style={{ textAlign: 'left' }}>保留到期</th>
                    <td style={{ fontSize: 13 }}>{formatDateTime(order.hold_expires_at)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <h2 style={{ fontSize: 18, margin: '0 0 12px' }}>商品列（{order.items.length}）</h2>
          <div className="table-card" data-testid="order-items-table">
            <table>
              <thead>
                <tr>
                  <th>商品</th>
                  <th>班次</th>
                  <th>票種</th>
                  <th style={{ textAlign: 'right' }}>數量</th>
                  <th style={{ textAlign: 'right' }}>unit_price</th>
                  <th style={{ textAlign: 'right' }}>line_total</th>
                  <th>套用 pricing rule</th>
                </tr>
              </thead>
              <tbody>
                {order.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      無 items（legacy demo / webhook path）
                    </td>
                  </tr>
                )}
                {order.items.map((it) => (
                  <tr key={it.id} data-testid={`item-row-${it.id}`}>
                    <td>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>
                        {pickI18n(it.product_name, it.product_sku ?? '—')}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--c-text-muted)',
                          fontFamily: 'monospace',
                        }}
                      >
                        {it.product_sku ?? ''}
                      </div>
                      {/*
                       * 套票在明細裡是 N 筆各指一個真實班次的列，各筆金額是打包價的
                       * 分攤結果、單看對不上任何牌價。不標出來的話，現場看到的是兩筆
                       * 毫無關聯又算不出來的訂購。
                       */}
                      {it.package_product_id !== null && (
                        <div
                          style={{ fontSize: 11, color: 'var(--c-accent, #b45309)', marginTop: 2 }}
                        >
                          套票：{pickI18n(it.package_name, it.package_sku ?? '—')}
                          （金額為打包價分攤）
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>{formatDateTime(it.schedule_departure_at)}</td>
                    <td style={{ fontSize: 13 }}>{ticketTypeLabel(it.ticket_type)}</td>
                    <td style={{ textAlign: 'right' }}>{it.quantity}</td>
                    <td
                      data-testid="item-unit-price"
                      style={{ textAlign: 'right', fontWeight: 600 }}
                    >
                      {formatTwdExact(it.unit_price)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatTwdExact(it.line_total)}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {it.pricing_id ? (
                        <>
                          <div data-testid="item-pricing-rule">{ruleSummary(it)}</div>
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--c-text-muted)',
                              fontFamily: 'monospace',
                            }}
                          >
                            {it.pricing_id}
                          </div>
                          {it.pricing_rule?.applicable_channels && (
                            <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                              通路：{it.pricing_rule.applicable_channels.join(', ')}
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: 'var(--c-text-muted)' }}>
                          無折扣（base_price fallback）
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: 16, fontSize: 12, color: 'var(--c-text-muted)' }}>
            <Link to="/admin/orders">← 返回列表</Link>
          </p>
        </>
      )}
    </div>
  );
}
